import { ClassGroupRI } from 'common-utils/rest-interfaces';

import { ClassPickerClass } from './interfaces/class-picker-class';
import { ClassPickerNavItem } from './interfaces/class-picker-nav-item';
import { ClassPickerStudent } from './interfaces/class-picker-student';
import { ClassSchedule } from './interfaces/class-schedule';
import { pdsI18n } from './pds-i18n';
import { pdsUtils } from './pds-utils';
import { PdsWidget } from './pds-widget';
import { SchedulingTerm } from './interfaces/scheduling-term';
import { TeacherRI } from 'common-utils/rest-interfaces';

const periodDayRegex = /^$|^PeriodAndDay$|^CustomPeriodDay$/; // empty string | period and day | custom period day
const escKey = 27;
const tabKey = 9;
declare let Polymer;

export class PdsClassPicker extends PdsWidget {
  is = 'pds-class-picker';

  attendanceStatusLink: ClassPickerNavItem;
  classSchedule: ClassSchedule;
  classSelector: HTMLElement;
  firstElement: HTMLElement;
  // when true the ui to switch between classes or groups is gone so only classes can be selected.
  isClassOnly: boolean;
  initiallySelectedData: {
    selectedStudent: string,
    selectedYear: string,
    selectedSchedulingTerm: string,
    showClasses: boolean,
    showGroups: boolean
  };
  initialVisit: boolean;
  lastElement: HTMLElement;
  links: Array<ClassPickerNavItem>;
  noClassesBlock: ClassGroupRI;
  possibleSchedulingTerms: Array<{ value: string, text: string }>;
  possibleYears: Array<{ value: string, text: string }>;
  properties = {
    isClassOnly: {
      type: Boolean,
      value: false
    },
    selectedStudent: {
      type: String,
      value: null
    },
    classSchedule: {
      type: String,
      observer: 'resetFocusOnClassSelector'
    },
    showGroups: {
      type: Boolean,
      observer: 'resetFocusOnClassSelector'
    }
  };
  getS3MediaPath: () => string;
  selectedClass: ClassPickerClass;
  selectedSchedulingTerm: string;
  // the selected student's uid
  selectedStudent: string;
  selectedYear: string;
  showClasses: boolean;
  showClassSelector: boolean;
  showGroups: boolean;
  // used to list the students in which the current user is looking at classes for (parent use case)
  studentOptions: Array<ClassPickerStudent>;
  // used for quick lookup of student data so that we can filter the sections
  studentMap: Map<string, ClassPickerStudent>;
  years: Array<string>;
  schedulingTerms: Array<SchedulingTerm>;
  hasMultipleSchools: boolean;
  kebyoardNavigationAndEscapeListener: EventListenerOrEventListenerObject | undefined;

  ready(): void {
    super.ready();
    this.getS3MediaPath = this.pds.preferences.getS3MediaPath;
    this.showClasses = true;
    this.possibleSchedulingTerms = [];
    this.possibleYears = [];
    this.studentMap = new Map<string, ClassPickerStudent>();
    this.addEventListener('pdsClassChange', this.classChangeEvent);
    this.noClassesBlock = {
      groupName: 'Continue With Student',
      groupType: 'None',
      sectionUids: []
    };
    //To prevent abrupt break of code until all applications implement multiple school context check and pass 'hasMultipleSchools' as input. Can be removed once it is done.
    if (this.hasMultipleSchools === undefined) {
      this.hasMultipleSchools = false;
    }
  }

  toggleClassSelector(event: Event): void {
    event.preventDefault();
    this.showClassSelector = !this.showClassSelector;
    if (this.showClassSelector) {
      // mark the class selector trigger button to be focused on when the global class picker closes
      // should there be another element in the future that causes the class selector to open
      // this selector should be changed to event.target instead.
      this.pds.utils.markFocusableElement(<HTMLElement>this.querySelector('#class-selector-trigger-button'));
      this.backupData();
      this.fire('pdsClassPickerOpen', true);
      this.initialVisit = true; // initial visit to class picker
      this.resetFocusOnClassSelector();
    } else {
      this.restoreData();
      let lastMarkedElement = this.pds.utils.getLastMarkedElement();
      if (lastMarkedElement != null) {
        lastMarkedElement.focus();
      }
      this.fire('pdsClassPickerClosed', {
        prevSelectedStudent: this.initiallySelectedData.selectedStudent,
        prevSelectedYear: this.initiallySelectedData.selectedYear,
        prevSelectedSchedulingTerm: this.initiallySelectedData.selectedSchedulingTerm
      });
      this.removeClassSelectorFocusProperties();
    }
  }

  /**
   * As the user interacts with the class picker, the items that are displayed to the user might change
   * depending on what he/she selects. This function is implemented for change detection so we can
   * have a real time total of the first and last element within the class picker.
   */

  resetFocusOnClassSelector(): void {
    setTimeout(() => {
      if (this.initialVisit) {
        // this gets called upon initial visit of the class picker, we grab a reference
        // to the entire class selector and handle the keyboard navigation
        this.classSelector = <HTMLElement>this.querySelector('.pds-class-selector');
        this.firstElement = <HTMLElement>this.querySelector('#class-selector-student-select');
        if (this.firstElement == null) {
          this.firstElement = <HTMLElement>this.querySelector('#class-selector-year-select');
        }
        if (this.firstElement != null) {
          this.firstElement.focus();
        }
        this.handleKeyBoardNavigationAndEscape();
        this.initialVisit = false;
      }
      this.lastElement = this.getLastElementInClassPicker();
    });
  }

  /**
   * This function gets called when we need to reset the first and last element. Since choosing the following changes
   * which classes are displayed:
   * 1) Changing the year in the year selector
   * 2) Changing the scheduling? term
   * 3) switching context to a group.
   */

  getLastElementInClassPicker(): HTMLElement {
    /**
     * this is a workaround to using: this.querySelector('pds-class-block:last-of-type');
     * since IE doesnt play well with custom selectors we're doing this the hard way.
     * 1) Each class block lies within a class called class-block-wrapper. If the user goes to the groups tab and decides to navigate back
     *    to a single section context, a class-block-wrapper exists (for the group), however, the display property in the style is set to none.
     * 2) We dont care about the class-block-wrappers where the display is none, because they dont show up so filter them out.
     * 3) Inside the class-block-wrapper, there are multiple pds-class-blocks so grab the last one.
     * 4) Look for a attendance link inside the last pds-class-block (chair or checkmark icon), if one exists thats your last element
     *    if one doesnt exist your last element is the ellipses.
     *    if both doesnt exist, then your last element is the actual class block :D
     * 5) If this fails in any of those 3 steps, return the message-info because there will be one displayed.
     */
    let myListOfClassBlocks = this.querySelectorAll('.class-block-wrapper');
    let myListOfDisplayedClassBlocks = new Array<HTMLElement>();
    // ie doesn't like me calling forEach on a NodeList<Elements>, so we have to treat it like an Array
    Array.prototype.forEach.call(myListOfClassBlocks, (eachClassBlock) => {
      if (eachClassBlock.style.display !== 'none') {
        myListOfDisplayedClassBlocks.push(<HTMLElement>eachClassBlock);
      }
    });
    if (myListOfDisplayedClassBlocks.length > 0) {
      let myClassBlockWrapper = myListOfDisplayedClassBlocks.pop();
      if (myClassBlockWrapper != null) {
        let myListOfClassBlocks = myClassBlockWrapper.querySelectorAll('pds-class-block');
        let myLastClassBlock = myListOfClassBlocks[myListOfClassBlocks.length - 1];
        if (myLastClassBlock != null) {
          // we found atleast one class block and we can proceed to get the last element
          let myAttendenceIcon = myLastClassBlock.querySelector('.pds-attendance-link');
          let myOptionsIcon = myLastClassBlock.querySelector('.class-block-options');
          if (myAttendenceIcon == null && myOptionsIcon == null) {
            return <HTMLElement>myLastClassBlock.querySelector('a');
          } else {
            return (myAttendenceIcon == null ? <HTMLElement>myOptionsIcon : <HTMLElement>myAttendenceIcon);
          }
        }
      }
    }
    return <HTMLElement>this.querySelector('.pds-message-info');
  }

  /**
   * This function handles the keyboard navigation when the class picker is active,
   * and hitting escape to close the class picker. A set timeout is added because the selectors arent yet
   * attached to the dom, causing them to be null.
   */

  handleKeyBoardNavigationAndEscape(): void {
    if (this.classSelector != null) {

      this.kebyoardNavigationAndEscapeListener = (event: KeyboardEvent) => {
        if (event.keyCode === escKey) {
          this.handleClassSelectorClose(event);
        }
        if (event.keyCode === tabKey) {
          if (!event.shiftKey && (event.target === this.lastElement)) {
            this.firstElement.focus();
            event.preventDefault();
          } else if (event.shiftKey && (event.target === this.firstElement)) {
            this.lastElement.focus();
            event.preventDefault();
          }
        }
      };

      this.classSelector.addEventListener('keydown', this.kebyoardNavigationAndEscapeListener);
    }
  }

  /**
   * This functions handles the scenario when the escape key is pressed in the class selector
   * causing it to close.
   * @param event
   */

  handleClassSelectorClose(event: any): void {
    event.stopImmediatePropagation();
    this.showClassSelector = !this.showClassSelector;
    this.restoreData();
    this.pds.utils.getLastMarkedElement().focus();
    this.fire('pdsClassPickerClosed', {
      prevSelectedStudent: this.initiallySelectedData.selectedStudent,
      prevSelectedYear: this.initiallySelectedData.selectedYear,
      prevSelectedSchedulingTerm: this.initiallySelectedData.selectedSchedulingTerm
    });
    this.removeClassSelectorFocusProperties();

  }

  removeClassSelectorFocusProperties(): void {
    this.classSelector.removeEventListener('keydown', <EventListenerOrEventListenerObject>this.kebyoardNavigationAndEscapeListener);
    delete this.initialVisit;
    delete this.classSelector;
    delete this.firstElement;
    delete this.lastElement;
  }

  // store previous selection before open class picker
  backupData(): void {
    this.initiallySelectedData = {
      selectedStudent: this.selectedStudent,
      selectedYear: this.selectedYear,
      selectedSchedulingTerm: this.selectedSchedulingTerm,
      showClasses: this.showClasses,
      showGroups: this.showGroups
    };
  }

  /**
   * restore previous selection when the class picker is closed by clicking the 'x' button
   * we only restore selectedStudent, selectedYear, selectedSchedulingTerm, showClasses and showGroups information.
   * after restore these properties, class picker will fire a custom event with all previous selection
   * for student, year and schedulingTerm to let others know the class picker is closed without saving.
   * since the component doesn't backup previous schedulingTerms and classSchedule information, it needs updated
   * schedulingTerms and classSchedule from your app which uses this component to work properly.
   */
  restoreData(): void {
    this.selectedStudent = this.initiallySelectedData.selectedStudent;
    this.selectedYear = this.initiallySelectedData.selectedYear;
    this.selectedSchedulingTerm = this.initiallySelectedData.selectedSchedulingTerm;
    this.showClasses = this.initiallySelectedData.showClasses;
    this.showGroups = this.initiallySelectedData.showGroups;
  }

  selectClasses(event: Event): void {
    event.preventDefault();
    this.showClasses = true;
    this.showGroups = false; // added a watch to this property
  }

  selectGroups(event: Event): void {
    event.preventDefault();
    this.showClasses = false;
    this.showGroups = true; // added a watch to this property
  }

  selectedClassDisplay(selectedClass: ClassPickerClass | ClassGroupRI): string {
    if (selectedClass == null || selectedClass === this.noClassesBlock) {
      return 'No Class Selected';
    }
    let classAsGroup = <ClassGroupRI>selectedClass;
    let classAsSection = <ClassPickerClass>selectedClass;
    if (classAsSection.schedulingTerm != null) {
      if (classAsSection.primaryDisplay != null) {
        return `${classAsSection.secondaryDisplay != null ? classAsSection.secondaryDisplay + ' ' : ''}${classAsSection.primaryDisplay}`;
      } else {  // if the user of the component didn't apply class preferences we default to periodDay in the display
        return `${classAsSection.periodDay != null ? classAsSection.periodDay + ' ' : ''}${classAsSection.courseName}`;
      }

    } else {
      if (classAsGroup.sectionUids != null) {
        return pdsI18n.getText('pds|class picker|xx - xx Classes', {
          arg0: classAsGroup.groupName,
          arg1: classAsGroup.sectionUids.length
        });
      } else {
        return `${classAsGroup.groupName}`;
      }
    }
  }

  classChangeEvent(event: Event): void {
    let newClass: ClassPickerClass = (<Event & { detail: ClassPickerClass }>event).detail;
    this.selectedClass = newClass;
    this.showClassSelector = false;
  }

  yearChangeEvent(event: Event): void {
    this.fire('pdsYearChange', this.selectedYear);
  }

  schedulingTermChangeEvent(event: Event): void {
    this.fire('pdsSchedulingTermChange', this.selectedSchedulingTerm);
  }

  studentChangeEvent(event: Event): void {
    this.fire('pdsStudentChange', this.selectedStudent);
  }

  classScheduleEmptyCheck(classSchedule: ClassSchedule): boolean {
    return classSchedule == null || classSchedule.sections == null || classSchedule.sections.length === 0;
  }

  classGroupingCheck(classGrouping: Array<ClassPickerClass>): boolean {
    return classGrouping != null && classGrouping.length > 0;
  }

  getPossibleYears(years: Array<string>): Array<{ value: string, text: string }> {
    // We do not want to replace the array because it causes styling issues in IE (stupid IE).
    // Instead we just clear out the array and re-populate.
    this.possibleYears.length = 0;
    for (let year of years) {
      this.possibleYears.push({
        text: year,
        value: year
      });
    }
    return this.possibleYears;
  }

  // returns a list of value text pairs for the dynamic options widget.
  // The term name is text. The value is the term name + schoolId(s)/schoolUid(s) for that term
  // Example: {text: "quarter 2", value: "quarter 2,111,222"} {text: "quarter 2", value: "quarter 2,1111-1111-1111-1111,2222-2222-2222-2222"}
  getPossibleSchedulingTerms(schedulingTerms: Array<SchedulingTerm>, hasMultipleSchools: boolean): Array<{ value: string, text: string }> {
    let newPossibleSchedulingTerms = new Array<{ value: string, text: string }>();

    for (let term of schedulingTerms) {
      let schoolIds: Array<string> = [];
      let schoolAbbreviation = '';

      for (let schoolDateRange of term.schoolDateRanges) {
        schoolIds.push(schoolDateRange.schoolUid);
      }

      if (hasMultipleSchools && term.schoolAbbreviation) {
        schoolAbbreviation = term.schoolAbbreviation.length <= 8 ? term.schoolAbbreviation : term.schoolAbbreviation.slice(0, 8);
        newPossibleSchedulingTerms.push({
          text: term.name + ' (' + schoolAbbreviation + ')',
          value: term.name + ',' + schoolIds.join(',')
        });
      } else {
        newPossibleSchedulingTerms.push({
          text: term.name,
          value: term.name + ',' + schoolIds.join(',')
        });
      }
    }

    this.possibleSchedulingTerms = newPossibleSchedulingTerms;
    return this.possibleSchedulingTerms;
  }

  // returns a list of value text pairs for the dynamic options widget. student's uid is value, firstname+lastname is text
  getStudentOptions(studentOptions: ClassPickerStudent[]): Array<{ value: string, text: string }> {
    let options: { value: string, text: string }[] = [];
    let optionsWithSections: { value: string, text: string }[] = [];
    let optionsWithoutSections: { value: string, text: string }[] = [];

    this.updateStudentMap();
    for (let student of studentOptions) {
      // Sort the students, displaying the students with sections first
      if (student.sectionUids && student.sectionUids.length > 0) {
        optionsWithSections.push({
          value: student.uid,
          text: `${student.firstName} ${student.lastName}`
        });
      } else {
        optionsWithoutSections.push({
          value: student.uid,
          text: `${student.firstName} ${student.lastName}`
        });
      }
    }
    options = optionsWithSections.concat(optionsWithoutSections);

    if (this.selectedStudent == null || this.selectedStudent.length === 0) {
      // if we have students but not selected student default to the first option(typically the first with sections)
      this.selectedStudent = options[0].value;
    }
    return options;
  }

  // TODO: temporary function to assign a random-ish color to the class or group.
  getSectionColorClass(sectionOrGroup: any): string {
    return pdsUtils.getSectionColorForClass(sectionOrGroup);
  }

  hasAllSectionsGroup(classSchedule: ClassSchedule, isClassOnly: boolean): boolean {
    if (classSchedule) {
      return isClassOnly && classSchedule.allClasses != null;
    } else {
      return false;
    }
  }

  getInfoMessage(selectedStudent: string): string {
    if (this.studentOptions != null && this.studentOptions.length > 0) {
      let student = this.studentMap.get(selectedStudent);
      return `${student != null ? student.firstName : 'The selected student '} has no classes scheduled during the current term.`;
    } else {
      return 'There are no classes in your schedule during the selected year.';
    }
  }

  getSecondaryTextForClassBlock(section: ClassPickerClass | ClassGroupRI): string {
    if (section === this.noClassesBlock) {
      return 'No Scheduled Classes';
    }

    let sectionAsClass = (<ClassPickerClass>section);
    if (sectionAsClass.secondaryDisplay != null && sectionAsClass.classDisplayPreference != null) {
      let prefix = periodDayRegex.test(sectionAsClass.classDisplayPreference) ? 'Period ' : '';
      return prefix + sectionAsClass.secondaryDisplay;
    }
    return sectionAsClass.periodDay != null ? 'Period ' + sectionAsClass.periodDay : '';
  }

  getPrimaryTextForClassBlock(section: ClassPickerClass | ClassGroupRI): string {
    let sectionAsClass = (<ClassPickerClass>section);
    if (sectionAsClass.primaryDisplay != null) {
      return sectionAsClass.primaryDisplay;
    }
    return sectionAsClass.courseName;
  }

  getPrimarySubTextForClassBlock(section: ClassPickerClass | ClassGroupRI): string | undefined {
    let sectionAsClass = <ClassPickerClass>section;
    if (sectionAsClass != null && sectionAsClass.teachers != null && sectionAsClass.teachers.length > 0) {
      let leadTeacher: TeacherRI | undefined = sectionAsClass.teachers.find(value => value.isLeadTeacher === true);
      if (leadTeacher != null) {
        return leadTeacher.firstName + ' ' + leadTeacher.lastName;
      }
    }
  }

  getSelectedStudentName(uid: string): string {
    this.updateStudentMap();
    if (this.studentMap.has(uid)) {
      let student = this.studentMap.get(uid);
      return `${student!.firstName} ${student!.lastName}`;
    }
    return '';
  }

  currentStudentHasPhoto(uid: string): boolean {
    this.updateStudentMap();
    if (this.studentMap.has(uid)) {
      let student = this.studentMap.get(uid);
      return student!.hasPhoto;
    }
    return false;
  }

  private updateStudentMap(): void {
    this.studentMap.clear();
    if (this.studentOptions != null && this.studentOptions.length > 0) {
      this.studentOptions.forEach((student) => {
        this.studentMap.set(student.uid, student);
      });
    }
  }
}
